/**
 * プロンプト管理ページ
 *
 * marketing-intelligence-hub のプロンプト管理と同 UX。カテゴリセクション →
 * プロンプトタイプカード (有効件数・変数チップ・用途別プロンプト一覧と有効/無効トグル・新規作成) →
 * 編集モーダル (systemPrompt / userPromptTemplate + 変数展開プレビュー)。
 *
 * カテゴリ内で複数のプロンプトを同時に有効化でき、有効が 2 件以上のときは AI が
 * 説明文 (variantDescription) を見て用途に合うものを選ぶ。
 *
 * @graph-stack creative
 * @graph-domain Marketing
 * @graph-business AIプロンプトのカテゴリ×用途別プロンプト管理画面。作成・編集・有効/無効切替・削除・変数展開プレビュー。
 * @graph-connects api [calls] prompts.listTypes / listVariants / upsertVariant / activateVariant / deactivateVariant / deleteVariant
 */
import { useToast } from '@cortex/web-design-system/components/ui/Toast';
import {
  renderPromptVariant,
  type CreativePromptType,
  type PromptTypeConfig,
  type PromptVariant,
} from '@cortex/domain-creative/web';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState, type JSX } from 'react';
import { orpc } from '../api/orpc.js';
import { useAuth } from '../components/auth-context.js';
import { PROMPT_CATEGORY_LABELS } from '../lib/labels.js';
import { buildPreviewVariables, findUndeclaredPlaceholders } from '../lib/prompt-utils.js';
import { formatDateTime } from '../lib/labels.js';
import {
  EmptyState,
  Field,
  GhostButton,
  GlassCard,
  INPUT_CLASS,
  Modal,
  PrimaryButton,
  SectionHeading,
} from '../components/ui.js';

/** バリアント編集ドラフト (新規は variantId: null)。 */
interface VariantDraft {
  promptType: CreativePromptType;
  variantId: string | null;
  variantName: string;
  variantDescription: string;
  systemPrompt: string;
  userPromptTemplate: string;
}

/**
 * 1 プロンプトタイプ分のカード。
 * @param props タイプ定義と編集ハンドラ
 * @returns カード要素
 * @graph-connects api [calls] prompts.listVariants / activateVariant / deactivateVariant / deleteVariant
 */
function PromptTypeCard(props: {
  config: PromptTypeConfig;
  canEdit: boolean;
  onEdit: (draft: VariantDraft) => void;
}): JSX.Element {
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const variantsQuery = useQuery(
    orpc.prompts.listVariants.queryOptions({ input: { promptType: props.config.type } }),
  );
  const variants = variantsQuery.data?.items ?? [];
  const activeCount = variants.filter((variant) => variant.isActive).length;

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: orpc.prompts.key() });
  };

  const activateMutation = useMutation(
    orpc.prompts.activateVariant.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        showToast('success', 'プロンプトを有効にしました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );
  const deactivateMutation = useMutation(
    orpc.prompts.deactivateVariant.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        showToast('success', 'プロンプトを無効にしました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );
  const deleteMutation = useMutation(
    orpc.prompts.deleteVariant.mutationOptions({
      onSuccess: async () => {
        await invalidate();
        showToast('success', 'バリアントを削除しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const variantIdOf = (variant: PromptVariant): string =>
    variant.id.replace(`${props.config.type}__`, '');

  return (
    <GlassCard className="mb-4">
      <div className="flex items-start justify-between">
        <div>
          <div className="text-title font-bold text-foreground">{props.config.name}</div>
          <p className="mt-1 text-xs text-subtle">{props.config.description}</p>
        </div>
        {props.canEdit && (
          <GhostButton
            onClick={() => {
              props.onEdit({
                promptType: props.config.type,
                variantId: null,
                variantName: '',
                variantDescription: '',
                systemPrompt: props.config.defaultSystemPrompt,
                userPromptTemplate: props.config.defaultUserPrompt,
              });
            }}
          >
            + 新規作成
          </GhostButton>
        )}
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {props.config.variables.map((variable) => (
          <span
            key={variable.name}
            title={variable.description}
            className="rounded border border-line-light bg-surface px-1.5 py-0.5 font-mono text-micro text-muted"
          >
            {`{{${variable.name}}}`}
          </span>
        ))}
      </div>

      <div className="mt-4 space-y-2">
        {variants.length === 0 && (
          <div className="text-xs text-muted">プロンプト未登録 (seed 未実行、または全削除済み)</div>
        )}
        {variants.map((variant) => (
          <div
            key={variant.id}
            className={`flex items-center gap-3 rounded-lg border px-3 py-2 ${
              variant.isActive ? 'border-ok-line-strong bg-ok-soft' : 'border-line-light bg-surface'
            }`}
          >
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2">
                <span className="truncate text-body font-semibold text-foreground">
                  {variant.variantName}
                </span>
                {variant.isActive && (
                  <span className="rounded-full bg-ok-soft px-2 py-0.5 text-micro font-bold text-ok">
                    有効
                  </span>
                )}
              </div>
              <div className="text-caption text-muted">
                {variant.variantDescription !== '' && (
                  <span className="mr-2">{variant.variantDescription}</span>
                )}
                {formatDateTime(variant.updatedAt)} / {variant.updatedBy}
              </div>
            </div>
            {props.canEdit && (
              <div className="flex shrink-0 gap-2 text-xs">
                {variant.isActive ? (
                  <button
                    type="button"
                    className="text-muted hover:text-foreground"
                    onClick={() => {
                      deactivateMutation.mutate({
                        promptType: props.config.type,
                        variantId: variantIdOf(variant),
                      });
                    }}
                  >
                    無効化
                  </button>
                ) : (
                  <button
                    type="button"
                    className="text-ok opacity-80 hover:opacity-100"
                    onClick={() => {
                      activateMutation.mutate({
                        promptType: props.config.type,
                        variantId: variantIdOf(variant),
                      });
                    }}
                  >
                    有効化
                  </button>
                )}
                <button
                  type="button"
                  className="text-muted hover:text-foreground"
                  onClick={() => {
                    props.onEdit({
                      promptType: props.config.type,
                      variantId: variantIdOf(variant),
                      variantName: variant.variantName,
                      variantDescription: variant.variantDescription,
                      systemPrompt: variant.systemPrompt,
                      userPromptTemplate: variant.userPromptTemplate,
                    });
                  }}
                >
                  編集
                </button>
                {!variant.isActive && (
                  <button
                    type="button"
                    className="text-ng opacity-70 hover:opacity-100"
                    onClick={() => {
                      deleteMutation.mutate({
                        promptType: props.config.type,
                        variantId: variantIdOf(variant),
                      });
                    }}
                  >
                    削除
                  </button>
                )}
              </div>
            )}
          </div>
        ))}
      </div>
      {activeCount === 0 && variants.length > 0 && (
        <p className="mt-2 text-caption text-caution">
          有効なプロンプトがありません。MCP はこのカテゴリのプロンプトを返せません。
        </p>
      )}
      {activeCount > 1 && (
        <p className="mt-2 text-caption text-muted">
          有効 {activeCount} 件。AI は説明文を見て用途に合うプロンプトを選ぶ。
        </p>
      )}
    </GlassCard>
  );
}

/**
 * プロンプト管理ページ。
 * @returns ページ要素
 * @graph-connects api [calls] prompts.listTypes / upsertVariant
 */
export function PromptsPage(): JSX.Element {
  const auth = useAuth();
  const queryClient = useQueryClient();
  const { showToast } = useToast();
  const typesQuery = useQuery(orpc.prompts.listTypes.queryOptions({ input: {} }));
  const types = typesQuery.data?.items ?? [];

  const [draft, setDraft] = useState<VariantDraft | null>(null);
  const [showPreview, setShowPreview] = useState(false);

  const upsertMutation = useMutation(
    orpc.prompts.upsertVariant.mutationOptions({
      onSuccess: async () => {
        await queryClient.invalidateQueries({ queryKey: orpc.prompts.key() });
        setDraft(null);
        showToast('success', 'プロンプトバリアントを保存しました');
      },
      onError: (error) => {
        showToast('error', error.message);
      },
    }),
  );

  const draftConfig = types.find((config) => config.type === draft?.promptType) ?? null;
  const undeclared =
    draft && draftConfig
      ? findUndeclaredPlaceholders(
          `${draft.systemPrompt}\n${draft.userPromptTemplate}`,
          draftConfig.variables,
        )
      : [];
  const preview =
    draft && draftConfig
      ? renderPromptVariant(
          { systemPrompt: draft.systemPrompt, userPromptTemplate: draft.userPromptTemplate },
          buildPreviewVariables(draftConfig.variables),
        )
      : null;

  const categories = [...new Set(types.map((config) => config.category))];

  return (
    <div>
      <SectionHeading title="Prompt Management" />
      <p className="mb-6 text-body text-muted">
        AI
        レビュー・クリエイティブ作成で使うプロンプトをカテゴリごとに管理。用途別に何個でも作れて、
        有効にしたプロンプトはすべて MCP 経由で AI に提供される (AI が説明文を見て選ぶ)。
      </p>
      {types.length === 0 && <EmptyState message="プロンプトタイプを読み込み中…" />}
      {categories.map((category) => (
        <div key={category} className="mb-8">
          <h3 className="mb-3 text-sm font-bold text-foreground">
            {PROMPT_CATEGORY_LABELS[category]}
          </h3>
          {types
            .filter((config) => config.category === category)
            .map((config) => (
              <PromptTypeCard
                key={config.type}
                config={config}
                canEdit={auth.canEdit}
                onEdit={setDraft}
              />
            ))}
        </div>
      ))}

      <Modal
        open={draft !== null}
        title={
          draft?.variantId === null
            ? `${draftConfig?.name ?? ''}: バリアントを新規作成`
            : `${draftConfig?.name ?? ''}: バリアントを編集`
        }
        onClose={() => {
          setDraft(null);
        }}
      >
        {draft && (
          <>
            <Field label="バリアント名">
              <input
                className={INPUT_CLASS}
                value={draft.variantName}
                onChange={(event) => {
                  setDraft({ ...draft, variantName: event.target.value });
                }}
              />
            </Field>
            <Field label="説明 (どういう用途のプロンプトか)">
              <input
                className={INPUT_CLASS}
                value={draft.variantDescription}
                onChange={(event) => {
                  setDraft({ ...draft, variantDescription: event.target.value });
                }}
              />
              <span className="mt-1 block text-xs text-subtle">
                同じカテゴリで複数有効化するときの、AI が用途に合うプロンプトを選ぶ判断材料になる。
                複数有効化するなら必ず書くこと。
              </span>
            </Field>
            <Field label="System Prompt">
              <textarea
                className={`${INPUT_CLASS} h-44 resize-y font-mono text-xs`}
                value={draft.systemPrompt}
                onChange={(event) => {
                  setDraft({ ...draft, systemPrompt: event.target.value });
                }}
              />
            </Field>
            <Field label="User Prompt Template">
              <textarea
                className={`${INPUT_CLASS} h-28 resize-y font-mono text-xs`}
                value={draft.userPromptTemplate}
                onChange={(event) => {
                  setDraft({ ...draft, userPromptTemplate: event.target.value });
                }}
              />
            </Field>
            {undeclared.length > 0 && (
              <p className="mb-3 text-xs text-caution">
                未宣言の変数: {undeclared.map((name) => `{{${name}}}`).join(', ')}
                (このタイプの変数として供給されないため原文のまま残ります)
              </p>
            )}
            <GhostButton
              onClick={() => {
                setShowPreview(!showPreview);
              }}
            >
              {showPreview ? 'プレビューを隠す' : '変数展開プレビュー'}
            </GhostButton>
            {showPreview && preview && (
              <pre className="mt-3 max-h-64 overflow-auto rounded-lg border border-line-light bg-surface p-4 font-mono text-caption leading-relaxed text-subtle">
                {`--- system ---\n${preview.systemPrompt}\n\n--- user ---\n${preview.userPrompt}`}
              </pre>
            )}
            <div className="mt-4 flex justify-end gap-2">
              <GhostButton
                onClick={() => {
                  setDraft(null);
                }}
              >
                キャンセル
              </GhostButton>
              <PrimaryButton
                disabled={
                  draft.variantName === '' ||
                  draft.systemPrompt === '' ||
                  draft.userPromptTemplate === '' ||
                  upsertMutation.isPending
                }
                onClick={() => {
                  upsertMutation.mutate({
                    promptType: draft.promptType,
                    variantId: draft.variantId,
                    input: {
                      variantName: draft.variantName,
                      variantDescription:
                        draft.variantDescription === '' ? undefined : draft.variantDescription,
                      systemPrompt: draft.systemPrompt,
                      userPromptTemplate: draft.userPromptTemplate,
                    },
                  });
                }}
              >
                保存
              </PrimaryButton>
            </div>
          </>
        )}
      </Modal>
    </div>
  );
}
